export interface ChartDatum { label: string; value: number }
interface ChartCaption { title: string; illustrative: boolean; source?: string }
export type ChatChartSpec = ChartCaption & (
  | { type: 'bar' | 'line'; unit?: string; data: ChartDatum[] }
  | { type: 'flow'; steps: string[] }
)

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const text = (value: unknown, limit: number): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= limit

/** Treat model output as data, including partial JSON arriving over the stream. */
export function parseChartSpec(raw: string): ChatChartSpec | null {
  if (raw.length > 12_000) return null
  let value: unknown
  try { value = JSON.parse(raw) } catch { return null }
  if (!record(value) || !text(value.title, 160) || typeof value.illustrative !== 'boolean') return null
  if (value.source !== undefined && !text(value.source, 500)) return null
  if (!value.illustrative && !text(value.source, 500)) return null
  const caption: ChartCaption = {
    title: value.title, illustrative: value.illustrative,
    ...(typeof value.source === 'string' ? { source: value.source } : {}),
  }
  if (value.type === 'flow') {
    if (!Array.isArray(value.steps) || value.steps.length < 2 || value.steps.length > 12 ||
      !value.steps.every((step) => text(step, 160))) return null
    return { ...caption, type: 'flow', steps: value.steps as string[] }
  }
  if (value.type !== 'bar' && value.type !== 'line') return null
  if (value.unit !== undefined && !text(value.unit, 40)) return null
  if (!Array.isArray(value.data) || value.data.length === 0 || value.data.length > 30) return null
  const data: ChartDatum[] = []
  for (const item of value.data) {
    if (!record(item) || !text(item.label, 120) || typeof item.value !== 'number' ||
      !Number.isFinite(item.value) || Math.abs(item.value) > 1e12) return null
    data.push({ label: item.label, value: item.value })
  }
  return { ...caption, type: value.type, data, ...(typeof value.unit === 'string' ? { unit: value.unit } : {}) }
}
